import type React from 'react'
import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useState,
} from 'react'

export type Palette = 'brand' | 'default'

const PALETTE_OPTIONS: Palette[] = ['brand', 'default']

interface PaletteContextType {
	palette: Palette
	setPalette: (palette: Palette) => void
}

const PaletteContext = createContext<PaletteContextType | undefined>(undefined)

export const PaletteProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [palette, setPalette] = useState<Palette>(() => {
		const saved = localStorage.getItem('palette')
		if (saved && PALETTE_OPTIONS.includes(saved as Palette)) {
			return saved as Palette
		}
		return 'brand'
	})

	useEffect(() => {
		localStorage.setItem('palette', palette)
		const root = document.documentElement
		root.removeAttribute('data-palette')

		if (palette === 'default') {
			root.setAttribute('data-palette', 'default')
		}
	}, [palette])

	return (
		<PaletteContext.Provider value={{ palette, setPalette }}>
			{children}
		</PaletteContext.Provider>
	)
}

export const usePalette = () => {
	const context = useContext(PaletteContext)
	if (context === undefined) {
		throw new Error('usePalette must be used within a PaletteProvider')
	}
	return context
}
